import React, { Component } from 'react';
import { Grid, Card, Typography, Button, Dialog, DialogContent, Toolbar, CardContent } from '@mui/material';
import { ErrorOutline as LoadErrorIcon } from '@mui/icons-material';
import Config from '../../config';
import axios from 'axios';
import EnhancedTable from '../../components/table';
import ContentLoader from '../../components/content_loader';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import Helpers, { formatCurrency } from '../../helpers';
import Status from '../../components/status';
import { FakeField } from '../../components/text_field';
import Address from '../../components/address';
import { InvoiceStatus } from './subscription_invoice';
import DownloadButton from '../../components/download_button';

const statusColors: Record<string, string> = {
    "Live": "#448044",
    "Cancelled": "#AAA",
    "Past Due": "#ee3224"
};

interface Subscription {
    Id?: string | number;
    Plan?: string;
    Status?: string;
    Amount?: number;
    Currency?: string;
    CardInfo?: string;
    BillToAddress?: Record<string, unknown>;
}

interface SubscriptionStatusProps {
    status?: string;
}

class SubscriptionStatus extends Component<SubscriptionStatusProps> {
    render(){
        let {status} = this.props;
        let color = statusColors[status as string];
        return <Status color={color} description={status}/>;
    }
}

interface SubscriptionDetailState {
    loaded: boolean;
    subscription: Subscription;
    showError: boolean;
    loadError?: boolean;
    errorContent?: string;
}

class SubscriptionDetail extends Component<{}, SubscriptionDetailState> {
    state: SubscriptionDetailState = {loaded: false, subscription: {}, showError: false};
    async componentDidMount(){
        let subId = new Helpers().queryString()['sub'];
        try{
            let sub: Subscription = (await axios.get(Config.api + `/odata/Company/Subscriptions('${subId}')`)).data;
            this.setState({subscription: sub, loaded: true});
        }catch{
            this.setState({loadError: true, loaded: true});
        }
    }
    updateCard = async () => {
        try {
            let url = (await axios.get(Config.api + `/odata/Company/Subscriptions('${this.state.subscription.Id}')/Functions.GetCardUpdateUrl?redirectUrl=${window.location.href}`)).data.value;
            window.location.href = url;
        } catch {
            this.setState({showError: true, errorContent: 'Failed to redirect to card update page.'});
        }
    }
    render(){
        let invoiceConfig = {
            columns: [
              { id: 'Id', label: 'ID', width: '12em', hidden: true },
              { id: 'Currency', label: 'Currency', width: '12em', hidden: true },
              { id: 'Number', label: 'Invoice Number', filterable: true },
              { id: 'Status', label: 'Status', filterable: true, template: (value: string) => <InvoiceStatus status={value}/> },
              { id: 'InvoiceDate', type:'date', filterable: true, sortable: true, label: 'Inoice Date', template: (value: string) => (<Moment format='MM/DD/YYYY'>{value}</Moment>), width: '15em' },
              { id: 'DueDate', type:'date', filterable: true, sortable: true, label: 'Due Date', template: (value: string) => value ? (<Moment format='MM/DD/YYYY'>{value}</Moment>) : '', width: '15em' },
              { id: 'Total', filterable: true, sortable: true, label: 'Total', width: '20em', template: (value: number, row: { Currency?: string }) => formatCurrency(value, row.Currency) },
              { id: 'Balance', filterable: true, sortable: true, label: 'Balance', width: '20em', template: (value: number, row: { Currency?: string }) => formatCurrency(value, row.Currency) },
              { command: 'commands', width: '10em', stopPropagation: 'true', template: (value: unknown, row: { Id: string | number }) => {return (
                  <><Button size='small' variant='outlined' component={Link} to={`/billing/subscription/invoice?id=${row.Id}`}>View</Button></>
              );}}
          ],
          url: Config.api + `/odata/Company/Subscriptions('${(this.state.subscription || {}).Id}')/Invoices`,
          order: 'desc',
          orderBy: 'Id',
          keyField: 'Id',
          pageSize: 10,
          pageSizes: [10, 20, 50],
          title: 'Invoices',
        };
        if(this.state.loadError){
            return <Grid container spacing={2}>
                <Grid size={12}>
                  <LoadErrorIcon style={{fontSize: '14rem', color: '#999'}}/>
                </Grid>
                <Grid size={12} style={{fontSize: '1.5rem', color: '#999'}}>{'Sorry, we could not load that subscription for you.'}</Grid>
          </Grid>;
        }
        if(!this.state.loaded){
            return <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '6em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '20em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '20em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
            </Grid>;
        }
        return <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <Toolbar className='lbdocbar'>
                        <span style={{textAlign: 'left', flex: 1}}>Subscription {this.state.subscription.Id}</span>
                        <DownloadButton color='primary' onClick={this.updateCard}>Update Card</DownloadButton>
                    </Toolbar>
                    <Toolbar className='lbstatusbar' style={{backgroundColor: statusColors[this.state.subscription.Status as string] || "#FFA500"}}>Status: {this.state.subscription.Status}</Toolbar>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }} style={{display: 'flex'}}>
                <Address disabled contact={this.state.subscription.BillToAddress || {}} label='Billing Address' style={{flex: 1}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>General Information</Toolbar>
                    <CardContent style={{display: 'flex', flexDirection: 'column'}}>
                        <FakeField label='Type'>{this.state.subscription.Plan}</FakeField>
                        <FakeField label='Amount'>{formatCurrency(this.state.subscription.Amount, this.state.subscription.Currency)}</FakeField>
                        <FakeField label='Credit card'>{this.state.subscription.CardInfo}</FakeField>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <EnhancedTable config={invoiceConfig}/>
            </Grid>
            <Dialog maxWidth="sm" open={this.state.showError} onClose={() => this.setState({showError: false})}>
                <Toolbar className='lbtoolbar'>Error</Toolbar>
                <DialogContent>
                  <Typography>{this.state.errorContent}</Typography>
                </DialogContent>
              </Dialog>
        </Grid>;
    }
}

export default SubscriptionDetail;

export { SubscriptionStatus };
export type { Subscription };
